import React, { useState, useEffect, useRef, useContext } from 'react';
import { Button, Table, Popconfirm, Form, Input, message } from 'antd';
import { Plus, Trash2, Menu } from 'lucide-react';
import AxiosCaller from '../../../authenticatie/AxiosCaller';
import { DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { openNotificationSuccess, openNotificationError } from '../../../Components/Notification/Notification';

const EditableContext = React.createContext(null);

// Custom Pointer Sensor to prevent dragging when interacting with specific elements
class CustomPointerSensor extends PointerSensor {
  static activators = [
    {
      eventName: 'onPointerDown',
      handler: ({ nativeEvent: event }) => {
        if (
          event.target.closest('.ant-color-picker') || 
          event.target.closest('.ant-color-picker-trigger') ||
          event.target.closest('input')
        ) {
          return false;
        }
        return true;
      },
    },
  ];
}

// Drag Handle Component
const DragHandle = ({ id }) => {
  const { attributes, listeners, setNodeRef } = useSortable({ id });
  return (
    <div 
      ref={setNodeRef} 
      {...attributes} 
      {...listeners} 
      style={{ cursor: 'move' }}
    >
      <Menu />
    </div>
  );
};

// Sortable Row Component
const Row = (props) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: props['data-row-key'],
  });

  const style = {
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging ? {
      position: 'relative',
      zIndex: 9999,
    } : {}),
  };

  const [form] = Form.useForm();
  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <tr {...props} ref={setNodeRef} style={style} {...attributes} {...listeners} />
      </EditableContext.Provider>
    </Form>
  );
};

// Editable Cell Component
const EditableCell = ({
  title,
  editable,
  children,
  dataIndex,
  record,
  handleSave,
  ...restProps
}) => {
  const [editing, setEditing] = useState(false);
  const colorInputRef = useRef(null);
  const inputRef = useRef(null);
  const form = useContext(EditableContext);
  
  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
    }
  }, [editing]);

  const toggleEdit = () => {
    setEditing(!editing);
    form.setFieldsValue({ [dataIndex]: record[dataIndex] });
  };

  const save = async () => {
    try {
      const values = await form.validateFields();
      toggleEdit();
      handleSave({ ...record, [dataIndex]: values[dataIndex] });
    } catch (errInfo) {
      console.error('Save failed:', errInfo);
    }
  };

  const handleColorBlockClick = () => {
    colorInputRef.current.click();
  };

  let childNode = children;

  if (editable) {
    if (dataIndex === 'kleur') {
      childNode = (
        <div 
          style={{ 
            position: 'relative', 
            display: 'inline-block',
            width: '20px',
            height: '20px',
          }}
          onClick={handleColorBlockClick}
        >
          <input
            ref={colorInputRef}
            type="color"
            value={record.kleur}
            onChange={(e) => handleSave({ ...record, kleur: e.target.value })}
            style={{ 
              width: '0', 
              height: '0', 
              opacity: 0,
              position: 'absolute',
            }}
          />
          <div 
            style={{
              backgroundColor: record.kleur,
              width: '20px',
              height: '20px',
              borderRadius: '5px',
              border: '1px solid #ccc',
              cursor: 'pointer',
            }}
          />
        </div>
      );
    } else if (dataIndex !== 'volgorde') {
      childNode = editing ? (
        <Form.Item
          style={{ margin: 0 }}
          name={dataIndex}
          rules={[{ required: true, message: `${title} is required.` }]}
        >
          <Input ref={inputRef} onPressEnter={save} onBlur={save} />
        </Form.Item>
      ) : (
        <div 
          className="editable-cell-value-wrap" 
          style={{ paddingInlineEnd: 24 }} 
          onClick={toggleEdit}
        >
          {children}
        </div>
      );
    }
  }

  return <td {...restProps}>{childNode}</td>;
};

const ProductLocatieScreen = () => {
  const { get, post, put, del } = AxiosCaller();
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchTypes();
  }, []);

  const fetchTypes = async () => {
    try {
      const response = await get('/Locatie');
     
      
      // Sort based on volgorde and ensure all items have a valid volgorde property
      const typesWithKeys = response.data.map((type) => ({
        ...type,
        key: type._id,
        volgorde: typeof type.volgorde === 'number' ? type.volgorde : 0
      })).sort((a, b) => a.volgorde - b.volgorde);
      
      setTypes(typesWithKeys);
    } catch (error) {
      console.error(error);
      openNotificationError("Fout bij ophalen van locaties", error)
    } finally {
      setLoading(false);
    }
  };

  // Function to normalize volgorde values (ensuring they are sequential from 0)
  const normalizeOrder = async (items) => {
    const normalizedItems = [...items].sort((a, b) => a.volgorde - b.volgorde)
      .map((item, index) => ({
        ...item,
        volgorde: index
      }));

    try {
      // Update all items with their normalized volgorde in the database
      await Promise.all(
        normalizedItems.map(async (item) => {
          await put(`/Locatie/${item._id}`, {
            naam: item.naam,
            kleur: item.kleur,
            volgorde: item.volgorde
          });
        })
      );
      
      return normalizedItems;
    } catch (error) {
      console.error('Error normalizing order:', error);
      openNotificationError("Fout bij normaliseren van volgorde", error);
      return items; // Return original items on error
    }
  };

  const handleSave = async (row) => {
    try {
      // Only update the specific field that was changed
      await put(`/Locatie/${row._id}`, { 
        naam: row.naam, 
        kleur: row.kleur, 
        volgorde: row.volgorde
      });
      
      const newData = [...types];
      const index = newData.findIndex((item) => item._id === row._id);
      if (index > -1) {
        newData.splice(index, 1, row);
        setTypes(newData);
      }
     
    } catch (error) {
      console.error('Error updating product locatie:', error);
      openNotificationError("Fout bij updaten van locatie", error);
    }
  };

  const handleDelete = async (key) => {
    try {
      await del(`/Locatie/${key}`);
      
      // Filter out the deleted item
      const filteredTypes = types.filter((item) => item.key !== key);
      
      // Normalize order after deletion
      const normalizedTypes = await normalizeOrder(filteredTypes);
      setTypes(normalizedTypes);
      
      openNotificationSuccess("Locatie succesvol verwijderd");
    } catch (error) {
      console.error('Error deleting product locatie:', error);
      openNotificationError("Fout bij verwijderen locatie", error);
    }
  };

  const handleAdd = async () => {
    try {
      // Find the highest volgorde value and add 1 to place at the end
      const newVolgorde = types.length > 0 
        ? Math.max(...types.map(t => t.volgorde)) + 1 
        : 0;
        
      const newLocatie = { 
        naam: 'Nieuwe Locatie', 
        kleur: '#CCCCCC', 
        volgorde: newVolgorde 
      }; 
      
      const response = await post('/Locatie', newLocatie);
      const newType = { 
        ...response.data, 
        key: response.data._id 
      };
      
      const updatedTypes = [...types, newType];
      setTypes(updatedTypes);
      
      // Optional: normalize the order to ensure consistency
      setTimeout(() => {
        normalizeOrder(updatedTypes)
          .then(normalizedTypes => setTypes(normalizedTypes));
      }, 500);
     
    } catch (error) {
      console.error('Error adding new locatie:', error);
      openNotificationError("Fout bij aanmaken van locatie", error);
    }
  };

  // Drag and Drop Sensors
  const sensors = useSensors(
    useSensor(CustomPointerSensor, {
      activationConstraint: {
        distance: 1,
      },
    })
  );

  // Drag End Handler
  const onDragEnd = async ({ active, over }) => {
    if (active.id !== over?.id) {
      // Find the indices of the dragged item and the target position
      const activeIndex = types.findIndex((i) => i.key === active.id);
      const overIndex = types.findIndex((i) => i.key === over?.id);
      
      // Create new array with the item moved
      const newTypes = arrayMove(types, activeIndex, overIndex);
      
      // Update volgorde values based on new positions
      const updatedTypes = newTypes.map((type, index) => ({
        ...type,
        volgorde: index 
      }));

      try {
        // Update UI immediately
        setLoading(true);
        setTypes(updatedTypes);

        // Update in the database
        await Promise.all(
          updatedTypes.map(async (type) => {
            await put(`/Locatie/${type._id}`, { 
              naam: type.naam, 
              kleur: type.kleur, 
              volgorde: type.volgorde
            });
          })
        );
        
        // Refetch to ensure consistency
        await fetchTypes();
      } catch (error) {
        console.error('Error updating order:', error);
        openNotificationError("Fout bij aanpassen van volgorde");
        // Revert to original order if update fails
        fetchTypes();
      } finally {
        setLoading(false);
      }
    }
  };

  // Columns configuration
  const columns = [
    {
      title: 'Verplaats',
      key: 'drag',
      width: 100,
      render: (_, record) => <DragHandle id={record.key} />,
    },
    {
      title: 'Naam',
      dataIndex: 'naam',
      editable: true,
      render: (naam, record) => (
        <p>{naam} <span style={{ color: '#888' }}>(volgorde: {record.volgorde +1})</span></p>
      )
    },
    {
      title: 'Kleur',
      dataIndex: 'kleur',
      editable: true,
      width: '120px',
      render: (color) => (
        <div
          style={{
            backgroundColor: color,
            width: '20px',
            height: '20px',
            borderRadius: '5px',
            border: '1px solid #ccc',
            display: 'inline-block',
          }}
        />
      ),
    },
    {
      title: 'Acties',
      width: '100px',
      key: 'actions',
      render: (_, record) => (
        <Popconfirm 
          title="Weet je zeker dat je deze locatie wilt verwijderen?" 
          onConfirm={() => handleDelete(record.key)}
        >
          <Button type="text" icon={<Trash2 className="delete-button" />} />
        </Popconfirm>
      ),
    },
  ];

  // Components for editable table
  const components = {
    body: {
      row: Row,
      cell: EditableCell,
    },
  };

  // Column configuration with editable cells
  const columnConfig = columns.map((col) => ({
    ...col,
    onCell: (record) => ({
      record,
      editable: col.editable,
      dataIndex: col.dataIndex,
      title: col.title,
      handleSave,
    }),
  }));

  if (error) return <div>{error}</div>;

  return (
    <DndContext 
      sensors={sensors} 
      modifiers={[restrictToVerticalAxis]} 
      onDragEnd={onDragEnd}
    >
      <SortableContext 
        items={types.map((i) => i.key)} 
        strategy={verticalListSortingStrategy}
      >
        <div className="Screen-container">
          <div className="space-between">
            <h1 className="title">Locaties</h1>
            <Button onClick={handleAdd} type="primary">
              <Plus /> Nieuwe Locatie
            </Button>
          </div>

          <Table
            components={components}
            rowClassName="editable-row"
            bordered
            dataSource={types}
            columns={columnConfig}
            pagination={false}
            scroll={{ y: 0.5 * window.innerHeight}}
            loading={loading} 
            rowKey="key"
          />
        </div>
      </SortableContext>
    </DndContext>
  );
};

export default ProductLocatieScreen;